/** Box blur — uniform-weighted separable convolution.
 *
 *  Implemented as two 1D passes (rows, then columns) using a
 *  running-sum sliding window. The sliding-window trick collapses
 *  the per-pixel kernel summation from O(r) reads to O(1), so total
 *  cost is O(W·H) independent of radius — meaningful since "Box
 *  Blur" is the cheap fallback users reach for when they want
 *  big-radius blur without Gaussian's per-pixel weight math.
 *
 *  Visually box blur is harsher than Gaussian (the uniform kernel
 *  has sharp falloff at the edges, producing a faintly boxy ring
 *  around bright pixels). Mostly useful as a building block for
 *  larger effects or as a fast preview of where blur will sit. */

export interface BoxBlurValue {
  /** Radius in pixels. 0 = identity. */
  radius: number;
}

export const identityBoxBlurValue = (): BoxBlurValue => ({ radius: 0 });

/** Box blur a single axis using a running sum. Clamp edges; pad the
 *  initial window by repeating the first sample.
 *
 *  Alpha is copied through unchanged — same reasoning as
 *  `gaussian-blur.ts` (avoid halos around transparent regions). */
function boxBlur1D(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number,
  horizontal: boolean,
): void {
  const r = Math.max(0, Math.floor(radius));
  if (r === 0) {
    dst.set(src);
    return;
  }
  const denom = 2 * r + 1;
  const lineLen = horizontal ? width : height;
  const otherLen = horizontal ? height : width;
  for (let other = 0; other < otherLen; other++) {
    let rSum = 0;
    let gSum = 0;
    let bSum = 0;
    // Prime the window with `r` copies of the first sample plus the
    // first `r+1` real samples (covers indices -r..r, clamped).
    for (let k = -r; k <= r; k++) {
      const clamped = Math.min(lineLen - 1, Math.max(0, k));
      const idx = horizontal ? (other * width + clamped) * 4 : (clamped * width + other) * 4;
      rSum += src[idx];
      gSum += src[idx + 1];
      bSum += src[idx + 2];
    }
    for (let i = 0; i < lineLen; i++) {
      const di = horizontal ? (other * width + i) * 4 : (i * width + other) * 4;
      dst[di] = rSum / denom;
      dst[di + 1] = gSum / denom;
      dst[di + 2] = bSum / denom;
      // Alpha pass-through. Source and destination indices match
      // (same pixel position; only the RGB channels are blurred).
      dst[di + 3] = src[di + 3];
      // Slide window: subtract sample leaving (i-r), add sample
      // entering (i+r+1). Clamp at edges.
      const leaving = Math.min(lineLen - 1, Math.max(0, i - r));
      const entering = Math.min(lineLen - 1, Math.max(0, i + r + 1));
      const lIdx = horizontal ? (other * width + leaving) * 4 : (leaving * width + other) * 4;
      const eIdx = horizontal ? (other * width + entering) * 4 : (entering * width + other) * 4;
      rSum += src[eIdx] - src[lIdx];
      gSum += src[eIdx + 1] - src[lIdx + 1];
      bSum += src[eIdx + 2] - src[lIdx + 2];
    }
  }
}

export function applyBoxBlurPixels(source: ImageData, value: BoxBlurValue): ImageData {
  const w = source.width;
  const h = source.height;
  const r = Math.max(0, Math.floor(value.radius));
  if (r === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const temp = new Uint8ClampedArray(source.data.length);
  const out = new Uint8ClampedArray(source.data.length);
  boxBlur1D(source.data, temp, w, h, r, true);
  boxBlur1D(temp, out, w, h, r, false);
  return new ImageData(out, w, h);
}

export function applyBoxBlurToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: BoxBlurValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyBoxBlurPixels(source, value), 0, 0);
}
